import { Analytics } from '@cobliteam/analytics';
import { buildUserInfoChanges, fromE164BR, toE164BR } from '@cobliteam/api-lib';
import type { Preferences } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useTranslation } from '@cobliteam/i18n';
import { updateAccountRequest } from '@cobliteam/store';
import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import Typography from '@cobliteam/ui/components/Typography';
import InputMask from '@cobliteam/ui/form/InputMask';
import Icon from '@cobliteam/ui/icons';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import WhatsappIcon from './elements/WhatsappIcon';

const PHONE_MASK = '(__) _____-____';
const PHONE_REPLACEMENT = { _: /\d/ };

const getLocalPhoneDigits = (value: string) => {
  const e164 = toE164BR(value);
  return e164.startsWith('55') ? e164.slice(2) : e164;
};

const WhatsAppPhoneNumberBanner = () => {
  const { t } = useTranslation('ai');
  const dispatch = useDispatch();
  const { getUser } = useUser();
  const user = getUser();
  const initialPhoneE164 = user.phoneNumber ?? '';
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(fromE164BR(initialPhoneE164));
  const hasRegisteredPhone = initialPhoneE164.length > 0;
  const isBannerDismissed = preferences?.hideAIWhatsappPhoneBanner === true;

  const nextPhoneE164 = toE164BR(phone);
  const phoneChanged = nextPhoneE164 !== initialPhoneE164;
  const isPhoneComplete = getLocalPhoneDigits(phone).length >= 10;
  const canSubmit = phoneChanged && isPhoneComplete;

  const handleOpen = () => {
    Analytics.track('AI - Chat - WhatsApp Phone Banner Clicked');
    setPhone(fromE164BR(user.phoneNumber ?? ''));
    setOpen(true);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    const changes = buildUserInfoChanges({
      initialName: user.name,
      initialPhone: initialPhoneE164,
      nextName: user.name,
      nextPhone: nextPhoneE164,
    });

    if (!('phone_number' in changes)) return;

    Analytics.track('AI - Chat - WhatsApp Phone Saved');
    Analytics.identify(user.id, { phone: nextPhoneE164 });

    dispatch(updateAccountRequest({ changes }));
    setOpen(false);
  };

  const handleDismiss = () => {
    setPreferences({ ...(preferences ?? ({} as Preferences)), hideAIWhatsappPhoneBanner: true });
    setOpen(false);
  };

  if (hasRegisteredPhone || isBannerDismissed) return null;

  return (
    <>
      <div className='mx-auto -mb-2 flex w-full'>
        <div className='relative flex h-10 w-full items-center rounded-t-lg border border-gray-6 border-b-0 bg-gray-1 text-gray-11 text-xs leading-4 transition-colors hover:bg-gray-2 hover:text-gray-12'>
          <button
            type='button'
            className='flex h-full w-full cursor-pointer items-center gap-1.5 px-4 pb-2 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-7'
            aria-label={t('whatsapp_invite_aria_label')}
            onClick={handleOpen}
          >
            <span className='truncate'>{t('whatsapp_invite_label')}</span>
            <span className='flex items-center gap-1'>
              <WhatsappIcon className='size-3.5 shrink-0' aria-hidden />
              <span className='shrink-0'>{t('whatsapp_invite_product')}</span>
            </span>
          </button>
          <Button
            type='button'
            variant='link'
            color='default'
            size='sm'
            icon={<Icon name='x' />}
            aria-label={t('whatsapp_invite_close_aria_label')}
            className='absolute top-1 right-2 size-6 text-gray-10 hover:text-gray-12'
            onClick={handleDismiss}
          />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <Dialog.Content size='sm' className='!w-[420px] max-w-[calc(100vw-32px)]'>
          <div className='relative px-5 pt-5 pr-12 pb-3'>
            <Dialog.Title className='sr-only'>{t('whatsapp_phone_modal.title')}</Dialog.Title>
            <Dialog.Description className='sr-only'>{t('whatsapp_phone_modal.description')}</Dialog.Description>
            <div className='flex items-start gap-3'>
              <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-[#25D366]/12'>
                <WhatsappIcon className='size-5' aria-hidden />
              </span>
              <div className='flex flex-col'>
                <Typography variant='heading-1' as='h2' className='text-gray-12'>
                  {t('whatsapp_phone_modal.title')}
                </Typography>
                <Typography variant='body' className='mt-1 max-w-[36ch] text-gray-11'>
                  {t('whatsapp_phone_modal.description')}
                </Typography>
              </div>
            </div>
            <Dialog.Close className='absolute top-3 right-3' />
          </div>

          <form onSubmit={handleSubmit}>
            <Dialog.Body className='flex flex-col gap-2 px-5 pt-2 pb-5'>
              <label htmlFor='ai-whatsapp-phone' className='flex flex-col gap-1.5'>
                <Typography variant='input-label' className='text-gray-12'>
                  {t('whatsapp_phone_modal.phone_label')}
                </Typography>
                <InputMask
                  id='ai-whatsapp-phone'
                  name='phone'
                  type='tel'
                  inputMode='tel'
                  autoComplete='tel'
                  mask={PHONE_MASK}
                  replacement={PHONE_REPLACEMENT}
                  value={phone}
                  placeholder={t('whatsapp_phone_modal.phone_placeholder')}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </label>
              <Typography variant='caption-default' className='text-gray-10'>
                {t('whatsapp_phone_modal.help_text')}
              </Typography>
            </Dialog.Body>

            <Dialog.Footer className='!py-4 flex justify-end gap-2'>
              <Dialog.Close asChild>
                <Button type='button' variant='ghost' color='default' size='sm'>
                  {t('whatsapp_phone_modal.cancel')}
                </Button>
              </Dialog.Close>
              <Button type='submit' size='sm' disabled={!canSubmit}>
                {t('whatsapp_phone_modal.save')}
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

export default WhatsAppPhoneNumberBanner;
